// client/src/components/pages/LexiStar/Reports/StandardReport.tsx
//
// /lexistar/reports/:key — a standard report (ST-00188 M4-R2; FR-RPT-002;
// m4-contracts §6–§7). The parameter form is generated from the definition's
// JSON schema (report_definitions.parameters, 028) and mirrored in the query
// string, so the address is shareable and is the page M4-R1's renderer prints
// (report_definitions.page + the parameters, ?print=1). The body is the
// provider's data — headline figures, a chart of the primary table, the
// tables, notes — the same data the XLSX/CSV exports carry. Export buttons run
// the report in the chosen format and download it.
//
// Reports with a page of their own (trip report, run summary, daily summary,
// certificate) show only their key parameter here and open that page.
//
// `?run=<run_id>` (how M4-R1's worker opens the page for a PDF): the run's
// pinned data snapshot (GET /reports/runs/:id/data) instead of live data, so
// the document is reproducible.

import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaDesktop, FaPlay, FaPrint } from 'react-icons/fa';
import ParameterForm from './ParameterForm';
import ReportBody from './ReportBody';
import { errorText, getReportData, getRunData, listDefinitions } from '../../../../services/lexistarReports';
import {
  DEDICATED_PAGES,
  FORMAT_LABELS,
  missingParams,
  normaliseProviderData,
  pageFor,
  paramsFromSearch,
  paramsToSearch,
  relativeLabel,
  schemaFields,
} from './reportLogic';
import {
  ExportButtons,
  PaperMessage,
  ReportDoc,
  ReportFrame,
  ReportRenderContext,
  toolbarButton,
  toolbarPrimary,
  usePrintMode,
  type LoadState,
} from './reportAtoms';
import { useAuth } from '../../../../types/commonComponents';
import type { ProviderReport, ReportDefinition, ReportFormat } from '../../../../types/lexistar-reports';

/** "2026-09-01 – 2026-09-07", "Last week (Mon–Sun)", or the day. */
function periodText(params: Record<string, unknown>, report: ProviderReport | null): string | null {
  if (report?.period) return report.period.label || `${report.period.from} – ${report.period.to}`;
  if (typeof params.period === 'string') return relativeLabel(params.period);
  if (typeof params.from === 'string' && typeof params.to === 'string') {
    return params.from === params.to ? params.from : `${params.from} – ${params.to}`;
  }
  if (typeof params.date === 'string') return params.date;
  return null;
}

function parameterSummary(def: ReportDefinition, params: Record<string, unknown>): string {
  const fields = schemaFields(def.parameters);
  return fields
    .filter((f) => f.kind !== 'daterange' && f.name !== 'date')
    .map((f) => {
      const v = params[f.name];
      if (v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length)) return null;
      const shown = Array.isArray(v) ? v.join(', ') : f.options.find((o) => o.value === String(v))?.label || String(v);
      return `${f.label}: ${shown}`;
    })
    .filter(Boolean)
    .join(' · ');
}

export default function StandardReport() {
  const { key = '' } = useParams();
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const print = usePrintMode();
  const rendering = useContext(ReportRenderContext);
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.reports.view');

  const [def, setDef] = useState<ReportDefinition | null>(null);
  const [defState, setDefState] = useState<LoadState>('loading');
  const [defError, setDefError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [result, setResult] = useState<{ load: LoadState | 'idle'; report: ProviderReport | null; error: string | null; at: string | null }>({
    load: 'idle',
    report: null,
    error: null,
    at: null,
  });

  // The definition (and its parameter schema).
  useEffect(() => {
    if (!canView) return undefined;
    let alive = true;
    listDefinitions()
      .then((defs) => {
        if (!alive) return;
        const d = defs.find((x) => x.key === key) || null;
        setDef(d);
        setDefState(d ? 'ready' : 'error');
        setDefError(d ? null : 'There is no report with this name.');
      })
      .catch((err) => {
        if (!alive) return;
        setDefState('error');
        setDefError(errorText(err, 'The report definitions could not be loaded.'));
      });
    return () => {
      alive = false;
    };
  }, [canView, key]);

  const fields = useMemo(() => schemaFields(def?.parameters), [def]);
  // The renderer opens `?print=1&run=<id>`: print that run's pinned snapshot.
  const runId = search.get('run');
  const [runParams, setRunParams] = useState<Record<string, unknown> | null>(null);
  const searchKey = search.toString();
  const applied = useMemo(() => paramsFromSearch(def?.parameters, new URLSearchParams(searchKey)), [def, searchKey]);
  const missing = useMemo(() => missingParams(fields, applied), [fields, applied]);
  const dedicated = def ? DEDICATED_PAGES[def.key] : undefined;

  useEffect(() => {
    setDraft(applied);
  }, [applied]);

  const load = useCallback(async () => {
    if (!def || dedicated || (!runId && missing.length)) return;
    setResult((r) => ({ ...r, load: 'loading', error: null }));
    try {
      if (runId) {
        const snap = await getRunData(runId);
        setRunParams(snap.parameters);
        setResult({ load: 'ready', report: normaliseProviderData(snap.data, snap.pinned), error: null, at: new Date().toISOString() });
      } else {
        const { data, pinned } = await getReportData(def.key, applied);
        setResult({ load: 'ready', report: normaliseProviderData(data, pinned), error: null, at: new Date().toISOString() });
      }
    } catch (err) {
      setResult({ load: 'error', report: null, error: errorText(err, 'The report could not be produced.'), at: null });
    }
  }, [def, dedicated, missing.length, applied, runId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (def) document.title = `${def.name} · LexiStar`;
  }, [def]);

  const apply = () => {
    if (!def) return;
    if (dedicated) {
      const target = pageFor(def, draft);
      if (target) navigate(target);
      return;
    }
    const next = new URLSearchParams(paramsToSearch(draft));
    if (search.get('print') === '1') next.set('print', '1');
    setSearch(next);
  };

  const setPrint = (on: boolean) => {
    const next = new URLSearchParams(search);
    if (on) next.set('print', '1');
    else next.delete('print');
    setSearch(next, { replace: true });
  };

  if (!canView) {
    return (
      <ReportFrame print={print} state="error" error="forbidden">
        <PaperMessage print={print} title="You do not have permission to view reports." />
      </ReportFrame>
    );
  }

  // The frame's readiness: the definition, then (unless a form is waiting) the data.
  const frameState: LoadState =
    defState !== 'ready'
      ? defState
      : dedicated || (!runId && missing.length)
        ? print
          ? 'error'
          : 'ready'
        : result.load === 'idle'
          ? 'loading'
          : result.load;
  const frameError =
    defError || result.error || (!runId && missing.length ? `Missing parameters: ${missing.join(', ')}` : null);
  const exportFormats = (def?.formats || []).filter((f): f is ReportFormat => f !== 'html');
  const draftMissing = missingParams(fields, draft);

  const toolbar = rendering ? null : (
    <>
      <Link to="/lexistar/reports" className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900">
        <FaArrowLeft size={10} /> Reports
      </Link>
      {def && !dedicated && (
        <div className="flex flex-wrap items-center gap-2">
          {print ? (
            <>
              <button type="button" className={toolbarButton} onClick={() => setPrint(false)}>
                <FaDesktop size={11} /> Screen view
              </button>
              <button type="button" className={toolbarButton} onClick={() => window.print()}>
                <FaPrint size={11} /> Print
              </button>
            </>
          ) : (
            <button type="button" className={toolbarButton} onClick={() => setPrint(true)} disabled={!!missing.length}>
              <FaPrint size={11} /> Print layout
            </button>
          )}
          <ExportButtons
            reportKey={def.key}
            parameters={applied}
            formats={exportFormats}
            disabled={result.load !== 'ready'}
            filenameBase={def.key}
            buttonClassName={toolbarPrimary}
          />
        </div>
      )}
    </>
  );

  const form =
    def && !rendering && !print ? (
      <section
        className="lxr-noprint max-w-[1180px] mx-auto mb-4 bg-surface border border-slate-200 rounded-xl shadow-sm p-4 sm:p-5"
        aria-label="Report parameters"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
          <div>
            <h1 className="text-lg font-bold text-slate-900">{def.name}</h1>
            {def.description && <p className="text-sm text-slate-600 max-w-3xl">{def.description}</p>}
          </div>
          <span className="text-xs text-slate-500">
            Formats: {def.formats.map((f) => FORMAT_LABELS[f] || f).join(' · ')}
          </span>
        </div>
        <ParameterForm
          fields={dedicated ? fields.filter((f) => f.name === dedicated.param) : fields}
          value={draft}
          onChange={setDraft}
          idPrefix={`report-${def.key}`}
          allowRelative={!dedicated}
        />
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button type="button" className={toolbarPrimary} onClick={apply} disabled={draftMissing.length > 0}>
            <FaPlay size={10} /> {dedicated ? `Open the ${def.name.toLowerCase()}` : 'Show report'}
          </button>
          {draftMissing.length > 0 && <span className="text-xs text-slate-500">Needs: {draftMissing.join(', ')}</span>}
        </div>
      </section>
    ) : null;

  const shown = runId && runParams ? runParams : applied;
  const period = def ? periodText(shown, result.report) : null;
  const summary = def ? parameterSummary(def, shown) : '';

  return (
    <ReportFrame print={print} state={frameState} error={frameError} toolbar={toolbar}>
      {form}
      {defState === 'loading' ? (
        <PaperMessage print={print} title="Loading the report…" />
      ) : !def ? (
        <PaperMessage print={print} title={defError || 'There is no report with this name.'}>
          <Link to="/lexistar/reports">See the reports centre</Link>
        </PaperMessage>
      ) : dedicated ? null : !runId && missing.length ? (
        print ? (
          <PaperMessage print={print} title={`Missing parameters: ${missing.join(', ')}`} />
        ) : null
      ) : result.load === 'loading' && !result.report ? (
        <PaperMessage print={print} title="Producing the report…" />
      ) : result.error || !result.report ? (
        <PaperMessage print={print} title={result.error || 'The report could not be produced.'} />
      ) : (
        <ReportDoc
          print={print}
          label={def.name}
          reportName="Standard report"
          title={result.report.title || def.name}
          subtitle={[result.report.subtitle || period, summary].filter(Boolean).join(' · ') || undefined}
          chips={
            result.report.state ? (
              <span className={`lxr-chip ${result.report.state === 'final' ? 'lxr-chip--ink' : 'lxr-chip--dashed'}`} data-state={result.report.state}>
                {result.report.state === 'final' ? '✓ Final' : '◌ Provisional'}
              </span>
            ) : undefined
          }
          generatedAt={result.report.generatedAt || result.at}
          footer={
            <>
              <span>
                {def.name} · ● measured · ◐ derived · ○ estimated
                {result.report.pinned ? ' · figures pinned to the aggregate and metric versions they came from' : ''}
              </span>
              <span>{result.report.state === 'final' ? 'Final — reproducible' : result.report.state === 'provisional' ? 'Provisional figures included' : ''}</span>
            </>
          }
        >
          <ReportBody report={result.report} print={print} />
        </ReportDoc>
      )}
    </ReportFrame>
  );
}
